import { useQuery } from "@tanstack/react-query";
import { Link } from "@/lib/router-compat";
import { BookOpen } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { articleKeywordsForTool } from "@/lib/blogToolMatch";
import { trackDynamicEvent } from "@/lib/analytics";
import { trackBlogToolClick } from "@/lib/blogToolEvents";

interface Props {
  /** Path of the tool page rendering this strip, e.g. "/tools/cash-to-close". */
  currentPath: string;
  title?: string;
}

/**
 * Closes the content flywheel in the other direction: tool pages already
 * link to sibling tools, this points them at the published articles on the
 * same topic so tool traffic feeds the blog too.
 */
const RelatedArticles = ({ currentPath, title = "Read the guide" }: Props) => {
  const keywords = articleKeywordsForTool(currentPath);

  const { data: posts = [] } = useQuery({
    queryKey: ["tool-related-articles", currentPath],
    enabled: keywords.length > 0,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const filter = keywords
        .slice(0, 6)
        .map((k) => `title.ilike.%${k}%`)
        .join(",");
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, slug, title, excerpt, category")
        .eq("status", "published")
        .or(filter)
        .order("published_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return data ?? [];
    },
  });

  if (posts.length === 0) return null;

  return (
    <section className="border-t border-border py-10">
      <div className="container mx-auto max-w-5xl px-4">
        <h2 className="mb-5 text-lg font-bold text-foreground">{title}</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {posts.map((p) => (
            <Link
              key={p.id}
              to={`/blog/${p.slug}`}
              onClick={() => {
                trackDynamicEvent("tool_related_article_click", { slug: p.slug, source: currentPath });
                trackBlogToolClick({ tool: `/blog/${p.slug}`, placement: "tool_page_articles", source: currentPath });
              }}
              className="group rounded-xl border border-border bg-card p-4 transition-colors hover:border-primary/40 hover:bg-muted/50"
            >
              <span className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary/80">
                <BookOpen size={13} /> {p.category}
              </span>
              <span className="block text-sm font-semibold leading-snug text-foreground">{p.title}</span>
              {p.excerpt && (
                <span className="mt-1 block line-clamp-2 text-xs leading-snug text-muted-foreground">{p.excerpt}</span>
              )}
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RelatedArticles;
